import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'

import { MobileTradingAccountCard } from '@pages/Traders-Room/Trading-Accounts/MobileTradingAccountCard'
import { TradingAccountsOutletCtx } from '@pages/Traders-Room/Trading-Accounts/TradingAccounts.page'

import { useProductReadContext } from '@context/ProductContext'

import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { TableNoResults } from '@ui/v2/Table/Table'

import { Operator, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { getScrollToCardId } from '@utils/getItemId'

import { AccountGroupType } from '@model/AccountGroupType'
import {
  isMT4PlatformType,
  isMT5PlatformType,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '@model/PlatformTypeEnum'
import {
  DemoTradingAccount,
  TradingAccount,
  TradingAccountStatusType,
  isTradingAccountStatusCloseOnly,
} from '@model/TradingAccount'

import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'

import { MobileDemoTradingAccountCard } from '../MobileDemoTradingAccountCard'
import { InactiveDemoTradingAccountsCards } from './InactiveDemoTradingAccountsCards'
import { InactiveLiveTradingAccountsCards } from './InactiveLiveTradingAccountsCards'

export const InactiveTradingAccountsPage: React.FC = () => {
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const { product } = useProductReadContext()
  const isMobile = useWindowResize()
  const { pageQuery, setPageQuery } = useOutletContext<TradingAccountsOutletCtx>()

  const { data: platformTypes = [] } = useQuery({
    queryKey: queryKeys.tradingAccount.platformTypes(product),
    queryFn: () => apiClient.getPlatformTypes(product),
  })

  const searchKey = { pageQuery }

  const inactiveLive = usePaginatedQuery<TradingAccount>({
    queryKey: queryKeys.tradingAccount.inactiveLive(searchKey),
    queryFn: (q) =>
      apiClient.getTradingAccounts({
        ...q,
        ...pageQuery,
        search: {
          AccountGroupType: { value: AccountGroupType.Live, operator: Operator.EQUAL },
          Status: { value: TradingAccountStatusType.Approved, operator: Operator.NOT_EQUAL },
        },
      }),
  })
  const onlyCFDInactiveLive = inactiveLive.data.filter(
    (acc) =>
      (isMT4PlatformType(acc.platformType.id) ||
        isMT5PlatformType(acc.platformType.id) ||
        isTMTPlatformType(acc.platformType.id) ||
        isTradingViewPlatformType(acc.platformType.id)) &&
      !isTradingAccountStatusCloseOnly(acc.status)
  )
  const inactiveDemo = usePaginatedQuery<DemoTradingAccount>({
    queryKey: queryKeys.tradingAccount.inactiveDemo(searchKey),
    queryFn: (q) =>
      apiClient.getDemoTradingAccounts({
        ...q,
        ...pageQuery,
        search: {
          Status: { value: TradingAccountStatusType.Approved, operator: Operator.NOT_EQUAL },
        },
      }),
  })
  const onlyCFDInactiveDemo = inactiveDemo.data.filter(
    (acc) =>
      (isMT4PlatformType(acc.platformType.id) ||
        isMT5PlatformType(acc.platformType.id) ||
        isTMTPlatformType(acc.platformType.id) ||
        isTradingViewPlatformType(acc.platformType.id)) &&
      !isTradingAccountStatusCloseOnly(acc.status)
  )

  const handleLivePageChange = (pageIndex: number, pageSize: number) => {
    const newPageQuery = {
      ...pageQuery,
      pageIndex,
      pageSize,
    }
    setPageQuery?.(newPageQuery)
    inactiveLive.setPageQuery?.(newPageQuery)
  }
  const handleDemoPageChange = (pageIndex: number, pageSize: number) => {
    const newPageQuery = {
      ...pageQuery,
      pageIndex,
      pageSize,
    }
    setPageQuery?.(newPageQuery)
    inactiveDemo.setPageQuery?.(newPageQuery)
  }

  const LiveContent = () =>
    isMobile ? (
      !onlyCFDInactiveLive.length && !inactiveLive.isLoading ? (
        <TableNoResults />
      ) : (
        onlyCFDInactiveLive.map((tradingAccount) => (
          <MobileTradingAccountCard
            key={tradingAccount.id}
            tradingAccount={tradingAccount}
            platformTypes={platformTypes}
          />
        ))
      )
    ) : (
      <InactiveLiveTradingAccountsCards
        tableQuery={{
          ...inactiveLive,
          data: onlyCFDInactiveLive,
        }}
        pageQuery={inactiveLive.pageQuery}
        setPageQuery={inactiveLive.setPageQuery}
        platformTypes={platformTypes}
      />
    )

  const DemoContent = () =>
    isMobile ? (
      !onlyCFDInactiveDemo.length && !inactiveDemo.isLoading ? (
        <TableNoResults />
      ) : (
        onlyCFDInactiveDemo.map((tradingAccount, index) => (
          <MobileDemoTradingAccountCard
            key={tradingAccount.id}
            tradingAccount={tradingAccount}
            isTradingInActive={true}
            cardId={getScrollToCardId(
              index,
              inactiveDemo.data.length,
              'inactive-demo-trading-accounts'
            )}
            platformTypes={platformTypes}
          />
        ))
      )
    ) : (
      <InactiveDemoTradingAccountsCards
        tableQuery={{
          ...inactiveDemo,
          data: onlyCFDInactiveDemo,
        }}
        pageQuery={inactiveDemo.pageQuery}
        setPageQuery={inactiveDemo.setPageQuery}
        platformTypes={platformTypes}
      />
    )

  return (
    <Loading isLoading={inactiveLive.isLoading || inactiveDemo.isLoading}>
      <LiveContent />
      {inactiveLive.meta && inactiveLive.hasInitialResults && (
        <Pagination
          scrollToHeaderId={ScrollToIds.InactiveLiveTradingAccountsHeader}
          maxPageSize={100}
          pageData={inactiveLive.meta}
          isLoading={inactiveLive.isLoading}
          onPageChanged={handleLivePageChange}
        />
      )}

      <PageSection title={t('Trading Account.Inactive Demo Trading Accounts')} />
      <DemoContent />
      {inactiveDemo.meta && inactiveDemo.hasInitialResults && (
        <Pagination
          scrollToHeaderId={ScrollToIds.InactiveDemoTradingAccountsHeader}
          maxPageSize={100}
          pageData={inactiveDemo.meta}
          isLoading={inactiveDemo.isLoading}
          onPageChanged={handleDemoPageChange}
        />
      )}
    </Loading>
  )
}
